// podtemat1_walec_types/VolumeOfCylinderTask.js
// Objętość walca: V = π r^2 h.
// Akceptujemy wynik dokładny (k*pi) lub liczbowy (±0.01).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import PiInput, { formatPiValueLatex } from "../../../../components/inputs/PiInput";

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const nearlyAbs = (a, b, eps) => Math.abs(a - b) <= eps;

function parseNumericOrKPi(raw) {
  if (!raw) return null;
  let s = String(raw).trim().replace(",", ".").replace(/\s/g, "");
  if (/^[\+\-]?\d+(\.\d+)?$/.test(s)) return { kind: "numeric", value: Number(s) };
  s = s.replace(/π/gi, "pi");
  let m = s.match(/^\(?([\+\-]?\d+(\.\d+)?)\)?\*?pi$/i); if (m) return { kind: "kpi", k: Number(m[1]) };
  m = s.match(/^pi\*?\(?([\+\-]?\d+(\.\d+)?)\)?$/i);     if (m) return { kind: "kpi", k: Number(m[1]) };
  return null;
}

export function generateTask() {
  const r = pick(range(2, 8));
  const h = pick(range(4, 12));
  const k = r * r * h;         // współczynnik przy π w V
  const numeric = k * Math.PI;
  return { type: "VolumeOfCylinderTask", data: { r, h, k, numeric } };
}

export function renderTask({ data }) {
  const { r, h } = data;
  return (
    <div style={{ marginBottom: 12 }}>
      Oblicz <b>objętość</b> walca o promieniu podstawy{" "}
      <InlineMath math={`${r}`} /> i wysokości <InlineMath math={`${h}`} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <PiInput
      value={userAnswer}
      onChange={(ans) => onChange?.(ans)}
      disabled={disabled}
      label="V"
      placeholder="np. 24π, 15*pi, 75,40"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const parsedFromPi = typeof userAnswer.parsed === "number" ? userAnswer.parsed : null;
  const fallbackParsed = (() => {
    if (!userAnswer?.raw) return null;
    const p = parseNumericOrKPi(userAnswer.raw);
    if (!p) return null;
    return p.kind === "kpi" ? p.k * Math.PI : p.value;
  })();

  const val = Number.isFinite(parsedFromPi) ? parsedFromPi : fallbackParsed;
  if (!Number.isFinite(val)) return false;

  const usedPi = /pi|π/i.test(String(userAnswer.raw || ""));
  const eps = usedPi ? 1e-6 : 0.0100001;
  return nearlyAbs(val, data.numeric, eps);
}

export function renderUserAnswer(userAnswer) {
  const raw = userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const valLatex = formatPiValueLatex(raw) || String(raw);
  return <InlineMath math={`\\displaystyle V = ${valLatex}`} />;
}

export function renderAnswer({ data }) {
  const exact = `V=${data.k}\\pi`;
  return <InlineMath math={exact} />;
}

export function renderExplanation({ data }) {
  const { r, h, k } = data;
  const step = `V=\\pi r^{2}h=\\pi\\cdot ${r}^{2}\\cdot ${h}=${k}\\pi`;
  return (
    <div style={{ display: "grid", rowGap: 8 }}>
      <p><strong>Krok:</strong> Podstaw do wzoru na objętość walca:</p>
      <BlockMath math={'{'+step+'}'} />
    </div>
  );
}
